// Detects when a chat input is a single domain — the trigger for the domain
// search panel. Ported verbatim from the old VisionHeight product: tolerates
// defanged domains (google[.]com), strips a scheme/path, and validates with the
// same regex. Mirrors detectIp's `extractSingleIp` contract.

// Defanged domains (common in threat intel) write the dot as [.] or (.) to
// prevent accidental clicks — normalize them back to a real dot.
export function removeBrackets(value: string): string {
  return value ? value.replace(/([([])\.([)\]])/g, '.') : value;
}

// The old product's validation regex. Note: it is intentionally permissive —
// it has no TLD list, so `report.pdf` also passes. That's fine on the INPUT
// path (a bad lookup is harmless); TLD filtering lives in rehypeDomainLinks,
// which scans free agent text where filenames are common.
const DOMAIN_REGEX = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;

export function isValidDomain(input: string): boolean {
  return DOMAIN_REGEX.test(removeBrackets(input).trim());
}

// Reduce a raw value to its bare host: undefang, drop the scheme, then cut at
// the first path/query/fragment/port/whitespace boundary.
export function extractDomain(value: string): string {
  if (!value) return '';
  let s = removeBrackets(value).trim();
  s = s.replace(/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//, '');
  s = s.split(/[/?#:\s]/)[0];
  return s;
}

/** If the whole input is a single domain (tolerating a `/domain ` prefix and a
 *  scheme/path), return the bare domain; otherwise null. Requires a single token
 *  so a sentence that merely contains a domain does not trigger — same as the IP
 *  path. IPv4s fail `isValidDomain` (numeric last label), so they never match. */
export function extractSingleDomain(text: string): string | null {
  const cleaned = text.trim().replace(/^\/domain\s+/i, '').trim();
  if (!cleaned || /\s/.test(cleaned)) return null;
  const domain = extractDomain(cleaned);
  return domain && isValidDomain(domain) ? domain : null;
}
